import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  WritableSignal,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { form } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  PropertyCharacteristicsDto,
  PropertyCommonAreaType,
  PropertyDefinitionDto,
  PropertyKeyInfoDto,
  PropertyRoomType,
  PropertyType,
} from '@workspace/data-types';
import { AbstractSignalFormComponent, AuraNumberInputComponent } from '@workspace/ng-aura';
import { EtxeEnumDisplayPipe } from '../../../../pipes/etxe-enum-display.pipe';
import { PropertyCharacteristicsForm } from '../../../../types/property-form.types';
import { PropertyFormBuilder } from '../../../../utils/property-form.builder';
import { PropertyEquipmentsListComponent } from '../../equipments-list/equipments-list.component';
import {
  PROPERTY_EQUIPMENT_ITEMS,
  PropertyEquipmentItem,
} from '../../equipments-list/property-equipment.constants';
import { PropertyParkingListComponent } from '../../parking-list/parking-list.component';
import { PropertySpacesListComponent } from '../../spaces-list/spaces-list.component';

@Component({
  selector: 'etxe-property-characteristics-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatButtonToggleModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    AuraNumberInputComponent,
    EtxeEnumDisplayPipe,
    PropertySpacesListComponent,
    PropertyEquipmentsListComponent,
    PropertyParkingListComponent,
  ],
  templateUrl: './characteristics-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyCharacteristicsFormComponent
  extends AbstractSignalFormComponent<PropertyCharacteristicsDto, PropertyCharacteristicsDto>
  implements OnInit
{
  private fb = inject(FormBuilder);

  propertyDefinition = input.required<PropertyDefinitionDto>();
  propertyKeyInfo = input<PropertyKeyInfoDto>();
  propertyIsCoOwnership = input<boolean>(false);
  isSkippable = input<boolean>(false);
  hideStepHeader = input<boolean>(false);

  isReady = output<boolean>();
  skipped = output<void>();

  // Expansion states
  expandedInterior = signal<boolean>(true);
  expandedExterior = signal<boolean>(true);
  expandedParking = signal<boolean>(true);
  expandedCommonAreas = signal<boolean>(true);
  expandedEquipments = signal<boolean>(true);

  roomTypes = Object.values(PropertyRoomType);
  commonAreaTypes = Object.values(PropertyCommonAreaType);
  equipmentItems: PropertyEquipmentItem[] = PROPERTY_EQUIPMENT_ITEMS;

  reactiveForm: FormGroup<PropertyCharacteristicsForm> = PropertyFormBuilder.buildCharacteristicsGroup(
    this.fb,
  ) as FormGroup<PropertyCharacteristicsForm>;

  override model: WritableSignal<PropertyCharacteristicsDto> = signal<PropertyCharacteristicsDto>(
    {} as PropertyCharacteristicsDto,
  );
  override formTree = form(this.model);

  private hasInitializedForm = false;

  readonly isHouse = computed(() => this.propertyDefinition()?.propertyType === PropertyType.HOUSE);

  readonly targetHabitableSurface = computed<number | null>(() => {
    const val = this.propertyKeyInfo()?.surface?.habitable;
    return typeof val === 'number' && val > 0 ? val : null;
  });

  readonly targetGroundSurface = computed<number | null>(() => {
    const val = this.propertyKeyInfo()?.surface?.ground;
    if (typeof val === 'number' && val > 0) return val;
    const hab = this.propertyKeyInfo()?.surface?.habitable;
    return typeof hab === 'number' && hab > 0 ? hab : null;
  });

  readonly targetLandSurface = computed<number | null>(() => {
    if (!this.isHouse()) return null;
    const val = this.propertyKeyInfo()?.surface?.land;
    return typeof val === 'number' && val > 0 ? val : null;
  });

  readonly totalInteriorSurface = computed<number>(() => {
    const interior = this.model()?.interior;
    if (!interior) return 0;
    let total = 0;
    const addSpaces = (list?: Array<{ surface?: number | null }>) => {
      if (Array.isArray(list)) {
        for (const item of list) {
          const s = Number(item?.surface);
          if (!Number.isNaN(s) && s > 0) {
            total += s;
          }
        }
      }
    };
    addSpaces(interior.rooms);
    addSpaces(interior.bedroom);
    addSpaces(interior.bathroom);
    addSpaces(interior.showerRoom);
    return Math.round(total * 100) / 100;
  });

  readonly remainingGroundSurface = computed<number | null>(() => {
    const target = this.targetGroundSurface();
    if (target === null) return null;
    const filled = this.totalInteriorSurface();
    return Math.round((target - filled) * 100) / 100;
  });

  /**
   * Surface au sol status:
   * - 'complete': difference is within 1% margin (or exactly 0)
   * - 'missing': total entered is below target by more than 1% margin
   * - 'exceeded': total entered exceeds target by more than 1% margin
   */
  readonly groundSurfaceStatus = computed<'complete' | 'missing' | 'exceeded' | null>(() => {
    const target = this.targetGroundSurface();
    if (target === null) return null;
    const filled = this.totalInteriorSurface();
    const tolerance = Math.max(0.1, target * 0.01);
    const diff = filled - target;

    if (Math.abs(diff) <= tolerance) {
      return 'complete';
    } else if (diff > tolerance) {
      return 'exceeded';
    } else {
      return 'missing';
    }
  });

  readonly groundSurfaceOverflow = computed<number | null>(() => {
    const target = this.targetGroundSurface();
    if (target === null) return null;
    const filled = this.totalInteriorSurface();
    const diff = filled - target;
    return diff > 0 ? Math.round(diff * 100) / 100 : null;
  });

  readonly groundProgressPercent = computed<number>(() => {
    const target = this.targetGroundSurface();
    if (!target || target <= 0) return 0;
    const filled = this.totalInteriorSurface();
    return Math.min(100, Math.max(0, Math.round((filled / target) * 100)));
  });

  readonly totalExteriorSurface = computed<number>(() => {
    const exterior = this.model()?.exterior;
    if (!exterior) return 0;
    let total = 0;
    const addSpaces = (list?: Array<{ surface?: number | null }>) => {
      if (Array.isArray(list)) {
        for (const item of list) {
          const s = Number(item?.surface);
          if (!Number.isNaN(s) && s > 0) {
            total += s;
          }
        }
      }
    };
    addSpaces(exterior.loggia);
    addSpaces(exterior.terrace);
    addSpaces(exterior.balcony);
    addSpaces(exterior.garden);
    addSpaces(exterior.swimmingPool);
    addSpaces(exterior.cellar);
    return Math.round(total * 100) / 100;
  });

  constructor() {
    super();

    effect(() => {
      const dto = this.dto();
      if (!this.hasInitializedForm) {
        if (dto && Object.keys(dto).length > 0) {
          this.reactiveForm = PropertyFormBuilder.buildCharacteristicsGroup(
            this.fb,
            dto,
          ) as FormGroup<PropertyCharacteristicsForm>;
        }
        this.reactiveForm.valueChanges.subscribe((val) => {
          this.model.set(val as PropertyCharacteristicsDto);
        });
        this.hasInitializedForm = true;
        this.model.set(this.reactiveForm.value as PropertyCharacteristicsDto);
        this.formReady.set(true);
      }
    });
  }

  get form() {
    return this.reactiveForm;
  }

  ngOnInit() {
    this.isReady.emit(true);
  }

  protected override buildModelFromDto(dto: PropertyCharacteristicsDto): PropertyCharacteristicsDto {
    return dto || ({} as PropertyCharacteristicsDto);
  }

  protected override buildDtoFromModel(model: PropertyCharacteristicsDto): PropertyCharacteristicsDto {
    return model;
  }

  toggleSection(section: 'interior' | 'exterior' | 'parking' | 'commonAreas' | 'equipments'): void {
    const sections: Record<string, { update: (fn: (v: boolean) => boolean) => void }> = {
      interior: this.expandedInterior,
      exterior: this.expandedExterior,
      parking: this.expandedParking,
      commonAreas: this.expandedCommonAreas,
      equipments: this.expandedEquipments,
    };
    if (sections[section]) {
      sections[section].update((v) => !v);
    }
  }

  getFormArray(path: string): FormArray {
    const control = this.reactiveForm.get(path);
    if (!(control instanceof FormArray)) {
      throw new Error(`Path ${path} is not a FormArray`);
    }
    return control;
  }

  addSpace(path: string) {
    const isRoom = path === 'interior.rooms';
    const isCoOwnership = this.propertyIsCoOwnership() || path.startsWith('exterior.');
    const group = isCoOwnership
      ? PropertyFormBuilder.buildSpaceInCoOwnershipGroup(this.fb, null, isRoom)
      : PropertyFormBuilder.buildSpaceGroup(this.fb, null, isRoom);
    this.getFormArray(path).push(group);
    this.model.set(this.reactiveForm.value as PropertyCharacteristicsDto);
  }

  removeSpace(path: string, index: number) {
    this.getFormArray(path).removeAt(index);
    this.model.set(this.reactiveForm.value as PropertyCharacteristicsDto);
  }

  addCommonArea() {
    this.form.controls.commonAreas.push(PropertyFormBuilder.buildSpaceGroup(this.fb, null, true));
    this.model.set(this.reactiveForm.value as PropertyCharacteristicsDto);
  }

  removeCommonArea(index: number) {
    this.form.controls.commonAreas.removeAt(index);
    this.model.set(this.reactiveForm.value as PropertyCharacteristicsDto);
  }
}
